// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@rpsc/App/Components/Common/XngZone";
import { GetIcon } from "@rpsc/Overrides/Widgets/Basic/SocialLinks";
import { DivWrapper, XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import { color, ellipsis } from "@rpsc/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    messages = "messages",
    message = "message",
    icon = "icon",
}
interface MessageFields {
    fields: {
        [fields.message]: string;
        [fields.icon]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.messages]: MessageFields[];
    };
}

type Props = OwnProps;
export interface SecondaryMessagesMessageStyles {
    wrapper?: InjectableCss;
    messageWrapper?: InjectableCss;
    closeClickable?: ClickablePresentationProps;
    closeIcon?: IconProps;
    message?: TypographyPresentationProps;
}

export const secondaryMessagesMessageStyles: SecondaryMessagesMessageStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    messageWrapper: {
        css: css`
            padding: 0 8px;
            display: flex;
            gap: 8px;
            align-items: center;
            &:not(:last-child) {
                border-right: 1px solid ${color("common.white")};
            }
            &:last-child {
                padding-right: 0;
            }
            &:first-child {
                padding-left: 0;
            }
            & > ${DivWrapper} > div {
                display: flex;
            }
            ${TypographyStyle} {
                margin: 0;
                font-size: 14px;
                line-height: 17px;
                ${ellipsis(1)}
                max-width: 30ch;
                text-wrap: nowrap;
            }
            &:hover {
                opacity: 0.5;
            }
        `,
    },
};

const styles = secondaryMessagesMessageStyles;
const SecondaryMessages: FC<Props> = ({ fields: { messages }, id }) => {
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="secondary-messages-widget">
            {messages.map(({ fields }, index) => (
                <StyledWrapper {...styles.messageWrapper} className="message-container" key={`message${index}`}>
                    <XngZone contentId={id} zoneName={`index-${index}`} />
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};
const widgetModule: WidgetModule = {
    component: SecondaryMessages,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        displayName: "Secondary Website Message",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.messages,
                displayName: "Messages",
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: () => translate("Message Item"),
                fieldType: "General",
                fieldDefinitions: [],
            },
        ],
    },
};
export default widgetModule;
